/**
 * Derive the desktop menu bar (tray) status from the live recording signals.
 *
 * The macOS shell shows a brand-themed icon whose look depends on a single
 * 4-state enum. This pure helper collapses the recorder's several lifecycle
 * signals (lifecycle phase, mic/auth error, stream health, WS reconnect, and the
 * system-audio tap phase) into that enum. Kept pure + unit-tested; the
 * {@link useDesktopTray} hook pushes the result to Rust.
 */

/** The four looks the tray icon can show. */
export type TTrayStatus = "idle" | "connecting" | "recording" | "error"

export interface ITraySignals {
  /** RecordingControls lifecycle state. */
  state: "idle" | "recording" | "paused" | "finalizing"
  /** Non-empty when the mic/auth/recording surfaced a hard error. */
  error: string
  /** Recording health check verdict. */
  health: "nominal" | "warning" | "error"
  /** True while the streaming WS is in reconnect backoff. */
  wsReconnecting: boolean
  /** System-audio (remote) tap phase: "detecting" = still hunting for meeting audio. */
  remotePhase: "idle" | "detecting" | "capturing"
}

/**
 * Precedence (first match wins):
 * 1. `idle`        — no live session.
 * 2. `error`       — a hard error while in a session (mic lost, backend/WS down, session expired).
 * 3. `connecting`  — session is settling: finalizing, WS reconnecting, or the tap still hunting.
 * 4. `recording`   — capturing with a healthy stream (also covers paused).
 */
export function deriveTrayStatus({ state, error, health, wsReconnecting, remotePhase }: ITraySignals): TTrayStatus {
  if (state === "idle") return "idle"
  if (error !== "" || health === "error") return "error"
  if (state === "finalizing" || wsReconnecting || remotePhase === "detecting") return "connecting"
  return "recording"
}
